import React, { useEffect, useState } from "react";
import api from "../api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { findCountry, flagOf } from "../site/countries";
import {
  Users, ShieldCheck, ListOrdered, Package, ShoppingBag, BarChart3,
  CheckCircle2, XCircle, Trash2, Plus, Search, Crown, AlertTriangle, Coins,
  Trophy, Award,
} from "lucide-react";
import { useT } from "../site/i18n";

/* ─── helpers ─── */
const Page = ({ title, sub, children, testId }) => (
  <div data-testid={testId}>
    <h2 className="page-title">{title}</h2>
    {sub && <p className="page-sub">{sub}</p>}
    {children}
  </div>
);
const Card = ({ children, style = {}, testId, ...p }) => (
  <div className="tg-card" data-testid={testId} style={{ padding: 22, ...style }} {...p}>{children}</div>
);
const fmt = (n, d = 2) => Number(n || 0).toLocaleString("ru-RU", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtInt = (n) => Number(n || 0).toLocaleString("ru-RU");

/* ════════ 1. ADMIN OVERVIEW ════════ */
export function AdminOverviewPage() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useAutoRefresh(() => api.get("/admin/dashboard").then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || t("common.error"))), 15000);

  if (err) return <Page title={t("adm.overview.title")} testId="admin-overview-page"><Card><div style={{ color: "#FF3D6C" }}><AlertTriangle size={16} style={{ verticalAlign: "middle", marginRight: 8 }} />{err}</div></Card></Page>;
  if (!data) return <Page title={t("adm.overview.title")} testId="admin-overview-page"><Card>{t("common.loading")}</Card></Page>;

  const kpi = [
    { l: t("adm.kpi.users"),         v: fmtInt(data.usersTotal),         c: "#00F2FF", ic: <Users size={18} /> },
    { l: t("adm.kpi.kyc_pending"),   v: fmtInt(data.usersKycPending),    c: "#FFD700", ic: <ShieldCheck size={18} /> },
    { l: t("adm.kpi.kyc_approved"),  v: fmtInt(data.usersKycApproved),   c: "#10F4A7", ic: <CheckCircle2 size={18} /> },
    { l: t("adm.kpi.banned"),        v: fmtInt(data.usersBanned),        c: "#FF3D6C", ic: <XCircle size={18} /> },
    { l: t("adm.kpi.transactions"),  v: fmtInt(data.transactionsTotal),  c: "#BC13FE", ic: <ListOrdered size={18} /> },
    { l: t("adm.kpi.orders"),        v: fmtInt(data.ordersTotal),        c: "#00F2FF", ic: <ShoppingBag size={18} /> },
    { l: t("adm.kpi.products"),      v: fmtInt(data.productsTotal),      c: "#FFD700", ic: <Package size={18} /> },
    { l: t("adm.kpi.merchants"),     v: fmtInt(data.merchantsTotal),     c: "#10F4A7", ic: <Crown size={18} /> },
  ];

  return (
    <Page title={t("adm.overview.title")} sub={t("adm.overview.sub")} testId="admin-overview-page">
      {/* GMV banner */}
      <Card style={{ background: "linear-gradient(135deg, #0D1B4B 0%, #1A3A8F 50%, #0E6B5E 100%)", marginBottom: 22, color: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.8, textTransform: "uppercase", letterSpacing: 2 }}>{t("adm.kpi.gmv")}</div>
            <div style={{ fontSize: 42, fontWeight: 900, marginTop: 6, letterSpacing: -1 }}>€{fmt(data.gmvEur)}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.8, textTransform: "uppercase", letterSpacing: 2 }}>{t("adm.kpi.circulating")}</div>
            <div style={{ fontSize: 42, fontWeight: 900, marginTop: 6, color: "#FFD700" }}>{fmtInt(data.circulatingTgc)}</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>≈ €{fmt(data.circulatingTgc * 0.10)}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.8, textTransform: "uppercase", letterSpacing: 2 }}>{t("adm.kpi.stakes")}</div>
            <div style={{ fontSize: 42, fontWeight: 900, marginTop: 6, color: "#10F4A7" }}>{fmtInt(data.stakesActive)}</div>
          </div>
        </div>
      </Card>

      <div className="dash-grid dash-grid-4" data-testid="admin-kpis">
        {kpi.map((k, i) => (
          <Card key={i} style={{ borderColor: `${k.c}33` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: k.c, marginBottom: 8 }}>
              {k.ic}<span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>{k.l}</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, fontFeatureSettings: '"tnum"' }}>{k.v}</div>
          </Card>
        ))}
      </div>
    </Page>
  );
}

/* ════════ 2. USERS MANAGEMENT ════════ */
const PAGE_SIZE = 25;
export function AdminUsersPage() {
  const { t } = useT();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [adjust, setAdjust] = useState(null);
  const [details, setDetails] = useState(null);        // {user, referrer, directReferrals, wallet, recentTransactions}
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [reattachEmail, setReattachEmail] = useState("");
  const [me, setMe] = useState(null);
  const [noRefOnly, setNoRefOnly] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [pwdShow, setPwdShow] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreselectId, setImportPreselectId] = useState(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const doExport = async ({ rootUserId = null, rootEmail = null } = {}) => {
    try {
      const params = new URLSearchParams();
      if (rootUserId) params.set("root_user_id", rootUserId);
      if (rootEmail) params.set("root_email", rootEmail);
      const qs = params.toString();
      const r = await api.get(`/admin/users/export${qs ? `?${qs}` : ""}`);
      const rows = r.data.users || [];
      if (rows.length === 0) { alert("Нет пользователей для экспорта в выбранной структуре."); return; }
      const cols = ["userId","email","firstName","lastName","phone","country","referralCode","referrerId","role","kycStatus","directRefCount","teamCount","createdAt"];
      const escape = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
      const csv = "\uFEFF" + cols.join(",") + "\n" + rows.map((row) => cols.map((c) => escape(row[c])).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const scopeTag = r.data.scope === "subtree" && r.data.root?.email ? `-${r.data.root.email.split("@")[0]}` : "-all";
      a.href = url; a.download = `tgc-users${scopeTag}-${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      const scopeLabel = r.data.scope === "subtree" ? `структура «${r.data.root?.email}»` : "вся организация";
      // Non-blocking toast-ish confirmation
      console.log(`Экспортировано ${rows.length} пользователей (${scopeLabel})`);
    } catch (e) { alert("Ошибка экспорта: " + (e.response?.data?.detail || e.message)); }
  };

  const promptExportByEmail = async () => {
    const email = window.prompt("Введите email пользователя, чью структуру нужно экспортировать (сам пользователь + вся его команда до 8 уровней):");
    if (!email) return;
    setExportMenuOpen(false);
    await doExport({ rootEmail: email.trim() });
  };

  const doCascadeDelete = async (userId, email) => {
    if (!window.confirm(`⚠️ КАСКАДНОЕ УДАЛЕНИЕ\n\nУдалить ${email} И ВСЮ ЕГО КОМАНДУ (все downstream рефералы по 8 уровням)?\n\nЭТО ДЕЙСТВИЕ НЕОБРАТИМО.`)) return;
    if (!window.confirm(`Подтвердите ещё раз: удалить ${email} и всех его рефералов рекурсивно?`)) return;
    try {
      const r = await api.delete(`/admin/users/${userId}/cascade`);
      alert(`✅ Удалено ${r.data.deletedUsers} пользователей, ${r.data.deletedWallets} кошельков, ${r.data.deletedTransactions} транзакций.`);
      setDetails(null); load(q, page, noRefOnly);
    } catch (e) { alert(e.response?.data?.detail || "Ошибка"); }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = async (query = "", pageNum = 1, noRef = false) => {
    setLoading(true);
    try {
      const skip = (pageNum - 1) * PAGE_SIZE;
      const r = await api.get(`/admin/users?q=${encodeURIComponent(query)}&limit=${PAGE_SIZE}&skip=${skip}&no_referrer=${noRef}`);
      setUsers(r.data.users || []); setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load("", 1, false);
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  // Auto-refresh every 15 seconds (pauses when tab hidden)
  useAutoRefresh(() => load(q, page, noRefOnly), 15000);

  // Search debounce — resets page to 1
  useEffect(() => {
    const tt = setTimeout(() => { setPage(1); load(q, 1, noRefOnly); }, 350);
    return () => clearTimeout(tt);
  }, [q, noRefOnly]);

  // Page changes — reload
  const goToPage = (p) => {
    if (p < 1 || p > pages || p === page) return;
    setPage(p); load(q, p, noRefOnly);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openDetails = async (userId) => {
    setDetailsLoading(true); setDetails({ loading: true, user: null });
    try {
      const r = await api.get(`/admin/users/${userId}/details`);
      setDetails(r.data); setReattachEmail("");
    } catch (e) {
      alert(e.response?.data?.detail || t("admin.c6fd3c"));
      setDetails(null);
    } finally { setDetailsLoading(false); }
  };

  const doReattach = async () => {
    if (!details?.user) return;
    const ok = window.confirm(`Переместить ${details.user.email} к новому реферреру ${reattachEmail || "(БЕЗ реферрера)"}?`);
    if (!ok) return;
    try {
      await api.post(`/admin/users/${details.user.userId}/reattach`, {
        newReferrerEmail: reattachEmail.trim() || null,
      });
      await openDetails(details.user.userId);
      load(q, page);
    } catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };

  const doDelete = async (userId, email) => {
    if (!window.confirm(`⚠️ Удалить ${email} навсегда? Это удалит юзера, его кошелёк и все транзакции. Нижестоящие рефералы останутся, но без привязки.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setDetails(null); load(q, page);
    } catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };

  const doSetPassword = async () => {
    if (!details?.user) return;
    if (!newPwd || newPwd.length < 6) { alert("Минимум 6 символов"); return; }
    if (!window.confirm(`Сменить пароль для ${details.user.email} на «${newPwd}»?`)) return;
    try {
      await api.post(`/admin/users/${details.user.userId}/set-password`, { newPassword: newPwd });
      alert(`✅ Пароль для ${details.user.email} сменён.\n\nНовый пароль: ${newPwd}\n\nСохраните его и передайте пользователю.`);
      setNewPwd("");
    } catch (e) { alert(e.response?.data?.detail || "Ошибка смены пароля"); }
  };

  const patch = async (userId, body) => {
    try { await api.patch(`/admin/users/${userId}`, body); load(q, page); }
    catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };

  const adjustWallet = async () => {
    if (!adjust) return;
    try {
      await api.post(`/admin/users/${adjust.userId}/wallet-adjust`, {
        wallet: adjust.wallet,
        deltaTgc: Number(adjust.deltaTgc),
        note: adjust.note || "Admin manual adjustment",
      });
      setAdjust(null); load(q, page);
    } catch (e) { alert(e.response?.data?.detail || t("admin.c6fd3c")); }
  };

  return (
    <Page title={t("adm.users.title")} sub={`${t("common.total")}: ${total}`} testId="admin-users-page">
      <Card style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input className="auth-input" placeholder={t("adm.users.search")} value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q, 1, noRefOnly)} style={{ flex: 1, minWidth: 200 }} data-testid="admin-users-search" />
          <button className="tg-btn primary" onClick={() => load(q, 1, noRefOnly)} data-testid="admin-users-search-btn"><Search size={14} /> {t("adm.users.find")}</button>
          <button onClick={() => setNoRefOnly(!noRefOnly)} data-testid="admin-users-no-referrer-filter"
            style={{
              padding: "10px 14px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700,
              border: `1px solid ${noRefOnly ? "#FF3D6C" : "var(--tg-border)"}`,
              background: noRefOnly ? "rgba(255,61,108,0.12)" : "transparent",
              color: noRefOnly ? "#FF3D6C" : "var(--tg-text-dim)",
              display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}>
            ⚠️ Без рекомендателя {noRefOnly && `(${total})`}
          </button>
          <button className="tg-btn ghost" onClick={() => { setImportPreselectId(null); setImportOpen(true); }} data-testid="admin-import-open"
            style={{ borderColor: "#00F2FF", color: "#00F2FF" }}>📥 Импорт CSV</button>
          <div style={{ position: "relative" }}>
            <button className="tg-btn ghost" onClick={() => setExportMenuOpen((v) => !v)} data-testid="admin-export-csv"
              style={{ borderColor: "#FFD700", color: "#FFD700" }}>📤 Экспорт CSV ▾</button>
            {exportMenuOpen && (
              <div data-testid="admin-export-menu"
                style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#0B1220", border: "1px solid #FFD700", borderRadius: 10, padding: 8, minWidth: 260, zIndex: 30, boxShadow: "0 8px 24px rgba(0,0,0,0.6)" }}>
                <button className="tg-btn ghost" data-testid="admin-export-all"
                  onClick={() => { setExportMenuOpen(false); doExport({}); }}
                  style={{ width: "100%", justifyContent: "flex-start", marginBottom: 6, borderColor: "transparent", color: "#FFD700", padding: "10px 12px" }}>
                  🌍 Вся организация
                </button>
                <button className="tg-btn ghost" data-testid="admin-export-by-email"
                  onClick={promptExportByEmail}
                  style={{ width: "100%", justifyContent: "flex-start", borderColor: "transparent", color: "#00F2FF", padding: "10px 12px" }}>
                  🎯 Структура по email…
                </button>
                <div style={{ fontSize: 11, color: "var(--tg-text-hint)", padding: "8px 4px 2px" }}>
                  Экспорт по структуре — сам пользователь + вся его команда (до 8 уровней).
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {importOpen && <BulkImportModal preselectReferrerId={importPreselectId} onClose={() => setImportOpen(false)} onDone={() => load(q, page, noRefOnly)} />}

      <Card>
        {loading && <div style={{ color: "var(--tg-text-hint)" }}>{t("common.loading")}</div>}
        <table className="dash-table">
          <thead>
            <tr><th>{t("adm.users.col.email")}</th><th>{t("adm.users.col.name")}</th><th>{t("adm.users.col.role")}</th><th>{t("adm.users.col.kyc")}</th><th>{t("adm.users.col.status")}</th><th>{t("adm.users.col.tgc")}</th><th>📅 Регистрация</th><th>{t("adm.users.col.actions")}</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.userId} data-testid={`admin-user-row-${u.userId}`}>
                <td style={{ fontWeight: 600 }}>{u.email}</td>
                <td>{u.firstName} {u.lastName}</td>
                <td>
                  <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                    background: u.role === "admin" ? "rgba(255,215,0,0.12)" : "rgba(0,242,255,0.08)",
                    color: u.role === "admin" ? "#FFD700" : "var(--tg-blue)" }}>
                    {u.role === "admin" ? "👑 admin" : "user"}
                  </span>
                </td>
                <td>
                  <KycBadge status={u.kycStatus} />
                </td>
                <td>
                  <span style={{ color: u.status === "banned" ? "#FF3D6C" : "#10F4A7", fontWeight: 700, fontSize: 12 }}>
                    ● {u.status === "banned" ? "ban" : "active"}
                  </span>
                </td>
                <td style={{ fontWeight: 700, color: "#FFD700" }}>{fmt(u.totalTgc)}</td>
                <td style={{ fontSize: 11, color: "var(--tg-text-dim)", whiteSpace: "nowrap" }} data-testid={`admin-user-created-${u.userId}`}>
                  {u.createdAt ? new Date(u.createdAt).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </td>
                <td style={{ display: "flex", gap: 4 }}>
                  {u.userId === me?.userId ? (
                    <span style={{ fontSize: 11, color: "var(--tg-text-hint)", fontStyle: "italic", padding: "4px 6px" }}>{t("adm.users.youlabel")}</span>
                  ) : (
                    <>
                      {u.kycStatus === "pending" && (
                        <>
                          <button className="dash-icon-btn" title="Approve KYC" onClick={() => approve(u.userId, load, q)} data-testid={`admin-kyc-approve-${u.userId}`} style={{ color: "#10F4A7" }}><CheckCircle2 size={14} /></button>
                          <button className="dash-icon-btn" title="Reject KYC" onClick={() => reject(u.userId, load, q)} data-testid={`admin-kyc-reject-${u.userId}`} style={{ color: "#FF3D6C" }}><XCircle size={14} /></button>
                        </>
                      )}
                      {u.status !== "banned" ? (
                        <button className="dash-icon-btn" title="Ban" onClick={() => patch(u.userId, { status: "banned" })} data-testid={`admin-ban-${u.userId}`} style={{ color: "#FF3D6C" }}>🚫</button>
                      ) : (
                        <button className="dash-icon-btn" title="Unban" onClick={() => patch(u.userId, { status: "active" })} data-testid={`admin-unban-${u.userId}`} style={{ color: "#10F4A7" }}>✓</button>
                      )}
                      <button className="dash-icon-btn" title="Adjust wallet" onClick={() => setAdjust({ userId: u.userId, wallet: "cash", deltaTgc: "100", note: "" })} data-testid={`admin-adjust-${u.userId}`}><Coins size={14} /></button>
                      <button className="dash-icon-btn" title="Структура / реферрер" onClick={() => openDetails(u.userId)} data-testid={`admin-details-${u.userId}`} style={{ color: "#00F2FF" }}><Users size={14} /></button>
                      <button className="dash-icon-btn" title="Удалить" onClick={() => doDelete(u.userId, u.email)} data-testid={`admin-delete-${u.userId}`} style={{ color: "#FF3D6C" }}><Trash2 size={14} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div data-testid="admin-pagination" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 18, flexWrap: "wrap" }}>
          <button className="tg-btn ghost" onClick={() => goToPage(page - 1)} disabled={page <= 1}
            data-testid="admin-page-prev" style={{ padding: "6px 14px", fontSize: 13 }}>‹ Назад</button>

          {(() => {
            const items = [];
            const push = (n) => items.push(n);
            const win = 1;
            push(1);
            if (page - win > 2) push("…");
            for (let p = Math.max(2, page - win); p <= Math.min(pages - 1, page + win); p++) push(p);
            if (page + win < pages - 1) push("…");
            if (pages > 1) push(pages);
            return items.map((it, i) => (
              it === "…"
                ? <span key={`g${i}`} style={{ color: "var(--tg-text-dim)", padding: "0 4px" }}>…</span>
                : <button key={it} onClick={() => goToPage(it)}
                    data-testid={`admin-page-${it}`}
                    style={{
                      minWidth: 36, padding: "6px 10px", borderRadius: 8, fontSize: 13,
                      border: "1px solid " + (it === page ? "#FFD700" : "var(--tg-border)"),
                      background: it === page ? "rgba(255,215,0,0.15)" : "transparent",
                      color: it === page ? "#FFD700" : "var(--tg-text)",
                      fontWeight: it === page ? 700 : 400, cursor: "pointer",
                    }}>{it}</button>
            ));
          })()}

          <button className="tg-btn ghost" onClick={() => goToPage(page + 1)} disabled={page >= pages}
            data-testid="admin-page-next" style={{ padding: "6px 14px", fontSize: 13 }}>Вперёд ›</button>

          <span style={{ marginLeft: 12, fontSize: 12, color: "var(--tg-text-hint)" }}>
            Стр. {page} из {pages} · всего {total}
          </span>
        </div>
      )}

      {/* wallet-adjust modal */}
      {adjust && (
        <div onClick={() => setAdjust(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} data-testid="admin-adjust-modal" style={{ width: 400, background: "rgba(11,11,16,0.98)", border: "1px solid var(--tg-border)", borderRadius: 16, padding: 24 }}>
            <h3 style={{ marginTop: 0 }}>{t("adm.adjust.title")}</h3>
            <div style={{ fontSize: 11, color: "var(--tg-text-dim)", marginBottom: 6 }}>{t("adm.adjust.wallet")}</div>
            <select className="auth-input" value={adjust.wallet} onChange={(e) => setAdjust({ ...adjust, wallet: e.target.value })} style={{ marginBottom: 12 }}>
              <option value="cash">Cash</option>
              <option value="shopping">Shopping</option>
              <option value="pension">Pension</option>
              <option value="insurance">Insurance</option>
            </select>
            <div style={{ fontSize: 11, color: "var(--tg-text-dim)", marginBottom: 6 }}>{t("adm.adjust.delta")}</div>
            <input className="auth-input" type="number" value={adjust.deltaTgc} onChange={(e) => setAdjust({ ...adjust, deltaTgc: e.target.value })} style={{ marginBottom: 12 }} data-testid="admin-adjust-delta" />
            <input className="auth-input" placeholder={t("adm.adjust.note")} value={adjust.note} onChange={(e) => setAdjust({ ...adjust, note: e.target.value })} style={{ marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="tg-btn ghost" onClick={() => setAdjust(null)} style={{ flex: 1 }}>{t("btn.cancel")}</button>
              <button className="tg-btn gold" onClick={adjustWallet} style={{ flex: 1 }} data-testid="admin-adjust-confirm">{t("adm.adjust.apply")}</button>
            </div>
          </div>
        </div>
      )}

      {/* User details / reattach modal */}
      {details && (
        <div onClick={() => setDetails(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} data-testid="admin-user-details-modal" style={{ width: 680, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", background: "rgba(11,11,16,0.98)", border: "1px solid var(--tg-border)", borderRadius: 16, padding: 24 }}>
            {detailsLoading || !details.user ? (
              <div style={{ color: "var(--tg-text-hint)" }}>{t("common.loading")}</div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                  <div>
                    <h3 style={{ margin: 0 }}>👤 {details.user.firstName} {details.user.lastName}</h3>
                    <div style={{ fontSize: 13, color: "var(--tg-text-hint)", marginTop: 4 }}>{details.user.email}</div>
                    <div style={{ fontSize: 11, color: "var(--tg-text-dim)", marginTop: 4, fontFamily: "monospace" }}>
                      userId: {details.user.userId} · ref-код: <b style={{ color: "#FFD700" }}>{details.user.referralCode}</b>
                    </div>
                  </div>
                  <button className="dash-icon-btn" onClick={() => setDetails(null)} style={{ fontSize: 20 }}>×</button>
                </div>

                {/* Referrer section */}
                <div style={{ padding: 14, background: "rgba(0,242,255,0.05)", borderRadius: 10, marginBottom: 16, border: "1px solid rgba(0,242,255,0.15)" }}>
                  <div style={{ fontSize: 11, color: "var(--tg-blue)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>⬆ Реферрер (кто пригласил)</div>
                  {details.referrer ? (
                    <div>
                      <b>{details.referrer.firstName} {details.referrer.lastName}</b>
                      <span style={{ color: "var(--tg-text-hint)", marginLeft: 8 }}>{details.referrer.email}</span>
                      <span style={{ color: "#FFD700", marginLeft: 8, fontFamily: "monospace", fontSize: 12 }}>{details.referrer.referralCode}</span>
                    </div>
                  ) : (
                    <div style={{ color: "var(--tg-text-dim)", fontStyle: "italic" }}>— прямая регистрация без реферрера</div>
                  )}
                  {/* Reattach controls */}
                  <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                    <input className="auth-input" placeholder="Email нового реферрера (пусто = открепить)" value={reattachEmail}
                      onChange={(e) => setReattachEmail(e.target.value)} style={{ flex: 1, padding: "8px 10px", fontSize: 13 }}
                      data-testid="admin-reattach-email" />
                    <button className="tg-btn primary" onClick={doReattach} style={{ padding: "6px 14px", fontSize: 13 }} data-testid="admin-reattach-confirm">
                      Перепривязать
                    </button>
                  </div>
                </div>

                {/* Direct referrals (L1) */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: "var(--tg-blue)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>
                    ⬇ Прямые рефералы (L1) · {details.directReferralsCount}
                  </div>
                  {details.directReferrals.length === 0 ? (
                    <div style={{ color: "var(--tg-text-dim)", fontStyle: "italic", fontSize: 13 }}>— нет прямых рефералов</div>
                  ) : (
                    <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--tg-border)", borderRadius: 8 }}>
                      {details.directReferrals.map((r) => (
                        <div key={r.userId} style={{ padding: "8px 12px", borderBottom: "1px solid var(--tg-border)", display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                          <div>
                            <b>{r.firstName} {r.lastName}</b>
                            <span style={{ color: "var(--tg-text-hint)", marginLeft: 8 }}>{r.email}</span>
                          </div>
                          <span style={{ fontFamily: "monospace", fontSize: 11, color: "var(--tg-text-dim)" }}>{r.referralCode}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Wallet */}
                {details.wallet && (
                  <div style={{ padding: 12, background: "rgba(255,215,0,0.05)", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                    <div style={{ fontSize: 11, color: "#FFD700", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>💰 Кошельки</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                      <div>Cash: <b>{fmt(details.wallet.cashTgc)}</b> TGC</div>
                      <div>Shopping: <b>{fmt(details.wallet.shoppingTgc)}</b> TGC</div>
                      <div>Pension: <b>{fmt(details.wallet.pensionTgc)}</b> TGC</div>
                      <div>Insurance: <b>{fmt(details.wallet.insuranceTgc)}</b> TGC</div>
                    </div>
                  </div>
                )}

                {/* Password reset block */}
                <div style={{ padding: 12, background: "rgba(0,242,255,0.04)", border: "1px solid rgba(0,242,255,0.2)", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                  <div style={{ fontSize: 11, color: "var(--tg-blue)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>🔐 Сменить пароль</div>
                  <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
                    <input className="auth-input" type={pwdShow ? "text" : "password"} placeholder="Новый пароль (мин. 6 символов)"
                      value={newPwd} onChange={(e) => setNewPwd(e.target.value)} style={{ flex: 1, paddingLeft: 14 }}
                      data-testid="admin-new-password" minLength={6} />
                    <button type="button" onClick={() => setPwdShow(!pwdShow)} data-testid="admin-pwd-toggle"
                      style={{ width: 36, borderRadius: 8, border: "1px solid var(--tg-border)", background: "rgba(0,0,0,0.3)", color: "var(--tg-text)", cursor: "pointer", fontSize: 14 }}>
                      {pwdShow ? "🙈" : "👁"}
                    </button>
                    <button className="tg-btn primary" onClick={doSetPassword}
                      style={{ padding: "6px 14px", fontSize: 13 }} data-testid="admin-set-password-confirm">
                      Применить
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginTop: 6 }}>
                    Запишите пароль — после применения он больше не отображается.
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 8, marginTop: 16, borderTop: "1px solid var(--tg-border)", paddingTop: 16, flexWrap: "wrap" }}>
                  <button className="tg-btn ghost" onClick={() => setDetails(null)} style={{ flex: 1, minWidth: 120 }}>Закрыть</button>
                  <button className="tg-btn ghost" data-testid="admin-details-export-structure"
                    onClick={() => doExport({ rootUserId: details.user.userId })}
                    style={{ flex: 1, minWidth: 180, borderColor: "#FFD700", color: "#FFD700" }}>
                    📤 Экспорт этой структуры
                  </button>
                  <button className="tg-btn ghost" data-testid="admin-details-import-under"
                    onClick={() => { setImportPreselectId(details.user.userId); setImportOpen(true); }}
                    style={{ flex: 1, minWidth: 180, borderColor: "#00F2FF", color: "#00F2FF" }}>
                    📥 Импорт в эту структуру
                  </button>
                  {details.user.userId !== me?.userId && details.user.role !== "admin" && (
                    <>
                      <button className="tg-btn" onClick={() => doDelete(details.user.userId, details.user.email)}
                        style={{ flex: 1, minWidth: 140, background: "#FF3D6C", color: "#fff" }} data-testid="admin-details-delete">
                        🗑 Удалить (только его)
                      </button>
                      <button className="tg-btn" onClick={() => doCascadeDelete(details.user.userId, details.user.email)}
                        style={{ flex: 1, minWidth: 180, background: "#7B0F2B", color: "#fff", border: "1px solid #FF3D6C" }} data-testid="admin-details-cascade-delete">
                        ☠️ Удалить с командой
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Page>
  );
}

/* ════════ BULK IMPORT MODAL ════════ */
function BulkImportModal({ onClose, onDone, preselectReferrerId = null }) {
  const [referrerEmail, setReferrerEmail] = useState("");
  const [referrerId, setReferrerId] = useState(preselectReferrerId || "");
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [rows, setRows] = useState([]);
  const [parseErr, setParseErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (preselectReferrerId) {
      (async () => {
        try {
          const r = await api.get(`/admin/users/${preselectReferrerId}/details`);
          if (r.data?.user) {
            setReferrerInfo(r.data.user);
            setReferrerEmail(r.data.user.email || "");
          }
        } catch (_) { /* preselect lookup best-effort */ }
      })();
    }
  }, [preselectReferrerId]);

  const lookupReferrer = async () => {
    if (!referrerEmail.trim()) { setReferrerInfo(null); setReferrerId(""); return; }
    setLookupBusy(true);
    try {
      const r = await api.get(`/admin/users?q=${encodeURIComponent(referrerEmail.trim())}&limit=5`);
      const match = (r.data.users || []).find((u) => (u.email || "").toLowerCase() === referrerEmail.trim().toLowerCase());
      if (match) { setReferrerInfo(match); setReferrerId(match.userId); }
      else { setReferrerInfo(null); setReferrerId(""); alert("Пользователь с таким email не найден"); }
    } catch (e) { alert("Ошибка поиска реферера"); }
    finally { setLookupBusy(false); }
  };

  const parseCsv = (text) => {
    setParseErr("");
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 2) { setRows([]); setParseErr("Нужен как минимум заголовок и одна строка данных"); return; }
    // Detect delimiter (comma vs semicolon)
    const delim = lines[0].includes(";") && !lines[0].includes(",") ? ";" : ",";
    const splitLine = (l) => {
      const out = []; let cur = ""; let inQ = false;
      for (let i = 0; i < l.length; i++) {
        const ch = l[i];
        if (ch === '"') { if (inQ && l[i + 1] === '"') { cur += '"'; i++; } else { inQ = !inQ; } }
        else if (ch === delim && !inQ) { out.push(cur); cur = ""; }
        else cur += ch;
      }
      out.push(cur); return out;
    };
    const header = splitLine(lines[0]).map((h) => h.trim().replace(/^\ufeff/, "").toLowerCase());
    const need = ["firstname", "lastname", "email"];
    for (const k of need) if (!header.includes(k)) { setRows([]); setParseErr(`Отсутствует обязательная колонка: ${k}`); return; }
    const idx = (k) => header.indexOf(k);
    const parsed = [];
    for (let li = 1; li < lines.length; li++) {
      const cells = splitLine(lines[li]);
      const row = {
        firstName: (cells[idx("firstname")] || "").trim(),
        lastName: (cells[idx("lastname")] || "").trim(),
        email: (cells[idx("email")] || "").trim().toLowerCase(),
        password: idx("password") >= 0 ? (cells[idx("password")] || "").trim() || null : null,
        country: idx("country") >= 0 ? ((cells[idx("country")] || "").trim().toUpperCase().slice(0, 2) || "KZ") : "KZ",
        phone: idx("phone") >= 0 ? (cells[idx("phone")] || "").trim() || null : null,
      };
      if (!row.email || !/@/.test(row.email)) continue;
      parsed.push(row);
    }
    setRows(parsed);
    if (parsed.length === 0) setParseErr("Не удалось распарсить ни одну строку");
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => { const txt = String(ev.target?.result || ""); setCsvText(txt); parseCsv(txt); };
    reader.readAsText(f, "utf-8");
  };

  const submit = async () => {
    if (!referrerId) { alert("Сначала выберите реферера (введите email и нажмите «Проверить»)"); return; }
    if (rows.length === 0) { alert("Нет строк для импорта"); return; }
    if (!window.confirm(`Импортировать ${rows.length} пользователей под ${referrerInfo?.email || referrerId}?`)) return;
    setSubmitting(true);
    try {
      const r = await api.post("/admin/users/import", { referrerId, users: rows });
      setResult(r.data);
      onDone && onDone();
    } catch (e) { alert(e.response?.data?.detail || "Ошибка импорта"); }
    finally { setSubmitting(false); }
  };

  const downloadTemplate = () => {
    const tmpl = "\ufefffirstName,lastName,email,password,country,phone\nИван,Иванов,ivan@example.com,,RU,+79001234567\nMaria,Garcia,maria@example.com,MyPass123,ES,\n";
    const blob = new Blob([tmpl], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "tgc-import-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-import-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 40, overflowY: "auto" }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "#0B1220", border: "1px solid #00F2FF", borderRadius: 14, padding: 28, width: "min(720px, 100%)", boxShadow: "0 24px 60px rgba(0,242,255,0.15)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 style={{ margin: 0, color: "#00F2FF", fontSize: 20 }}>📥 Массовый импорт пользователей</h3>
          <button onClick={onClose} data-testid="admin-import-close"
            style={{ background: "transparent", border: "none", color: "#888", fontSize: 24, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ color: "var(--tg-text-hint)", fontSize: 12, marginBottom: 18 }}>
          Пользователи будут созданы под выбранным реферером. Дубликаты по email пропускаются. Автоматически генерируется пароль, если поле пустое.
        </div>

        {!result && (
          <>
            {/* Referrer selection */}
            <div style={{ padding: 14, background: "rgba(0,242,255,0.05)", border: "1px solid rgba(0,242,255,0.25)", borderRadius: 10, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#00F2FF", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>
                🎯 Целевая структура — реферер {preselectReferrerId && "(предвыбран из деталей)"}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="auth-input" placeholder="email реферера, под которого добавить пользователей"
                  value={referrerEmail} onChange={(e) => setReferrerEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && lookupReferrer()}
                  disabled={!!preselectReferrerId}
                  data-testid="admin-import-referrer-email" style={{ flex: 1, paddingLeft: 14 }} />
                <button className="tg-btn primary" onClick={lookupReferrer} disabled={lookupBusy || !!preselectReferrerId}
                  data-testid="admin-import-lookup" style={{ padding: "6px 14px", fontSize: 13 }}>
                  {lookupBusy ? "…" : "Проверить"}
                </button>
              </div>
              {referrerInfo && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#10F4A7" }} data-testid="admin-import-referrer-info">
                  ✓ Реферер найден: <b>{referrerInfo.firstName} {referrerInfo.lastName}</b> · {referrerInfo.email} · код <code style={{ background: "rgba(0,0,0,0.4)", padding: "2px 6px", borderRadius: 4 }}>{referrerInfo.referralCode}</code>
                </div>
              )}
            </div>

            {/* CSV input */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ fontSize: 11, color: "#FFD700", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>📄 CSV данные</div>
                <button onClick={downloadTemplate} data-testid="admin-import-template"
                  style={{ background: "transparent", border: "1px solid #FFD700", color: "#FFD700", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11 }}>
                  ⬇ Скачать шаблон
                </button>
              </div>
              <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="admin-import-file"
                style={{ marginBottom: 8, color: "var(--tg-text-hint)", fontSize: 12 }} />
              <textarea value={csvText}
                onChange={(e) => { setCsvText(e.target.value); parseCsv(e.target.value); }}
                placeholder="firstName,lastName,email,password,country,phone&#10;Иван,Иванов,ivan@example.com,,RU,+79001234567"
                data-testid="admin-import-textarea"
                rows={7}
                style={{ width: "100%", background: "rgba(0,0,0,0.4)", border: "1px solid var(--tg-border)", borderRadius: 8, padding: 10, color: "var(--tg-text)", fontFamily: "monospace", fontSize: 12, resize: "vertical" }} />
              {parseErr && <div style={{ color: "#FF3D6C", fontSize: 12, marginTop: 6 }} data-testid="admin-import-parse-err">⚠ {parseErr}</div>}
            </div>

            {/* Preview */}
            {rows.length > 0 && (
              <div style={{ marginBottom: 14, padding: 10, background: "rgba(255,215,0,0.04)", border: "1px solid rgba(255,215,0,0.2)", borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: "#FFD700", fontWeight: 700, marginBottom: 6 }} data-testid="admin-import-count">
                  К импорту: {rows.length} записей
                </div>
                <div style={{ maxHeight: 140, overflowY: "auto", fontSize: 11, fontFamily: "monospace", color: "var(--tg-text-hint)" }}>
                  {rows.slice(0, 20).map((r, i) => (
                    <div key={i}>{i + 1}. {r.firstName} {r.lastName} · {r.email} · {r.country}{r.password ? " · [pwd]" : " · [auto-pwd]"}</div>
                  ))}
                  {rows.length > 20 && <div>… и ещё {rows.length - 20}</div>}
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid var(--tg-border)", paddingTop: 14 }}>
              <button className="tg-btn ghost" onClick={onClose}>Отмена</button>
              <button className="tg-btn primary" onClick={submit} disabled={submitting || !referrerId || rows.length === 0}
                data-testid="admin-import-submit">
                {submitting ? "Импортирую…" : `Импортировать ${rows.length || ""} пользователей`}
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="admin-import-result">
            <div style={{ padding: 14, background: "rgba(16,244,167,0.08)", border: "1px solid #10F4A7", borderRadius: 10, marginBottom: 14 }}>
              <div style={{ color: "#10F4A7", fontWeight: 700, fontSize: 16 }}>
                ✅ Создано {result.created} из {result.total}
              </div>
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto", fontSize: 12, fontFamily: "monospace", background: "rgba(0,0,0,0.4)", padding: 10, borderRadius: 8 }}>
              {(result.results || []).map((r, i) => (
                <div key={i} style={{ color: r.status === "created" ? "#10F4A7" : r.status === "skipped" ? "#FFD700" : "#FF3D6C", marginBottom: 3 }}>
                  {r.status === "created" ? "✓" : r.status === "skipped" ? "↷" : "✗"} {r.email}
                  {r.status === "created" && r.generatedPassword && <span style={{ color: "#00F2FF" }}> — пароль: <b>{r.generatedPassword}</b></span>}
                  {r.status !== "created" && r.reason && <span style={{ color: "var(--tg-text-hint)" }}> — {r.reason}</span>}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
              <button className="tg-btn ghost" onClick={() => { setResult(null); setRows([]); setCsvText(""); }}>Импортировать ещё</button>
              <button className="tg-btn primary" onClick={onClose} data-testid="admin-import-done">Готово</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


const KYC_LABEL = { not_started: "—", pending: "ожидает", approved: "✓", rejected: "✗" };
const KYC_COLOR = { not_started: "#888", pending: "#FFD700", approved: "#10F4A7", rejected: "#FF3D6C" };
const KycBadge = ({ status }) => (
  <span style={{ color: KYC_COLOR[status] || "#888", fontWeight: 700, fontSize: 12 }}>● {KYC_LABEL[status] || status}</span>
);
const approve = async (uid, load, q) => { await api.post(`/admin/kyc/${uid}/approve`); load(q); };
const reject  = async (uid, load, q) => { await api.post(`/admin/kyc/${uid}/reject`); load(q); };

/* ════════ 3. KYC QUEUE ════════ */
export function AdminKycPage() {
  const { t } = useT();
  const [users, setUsers] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/kyc/pending");
    setUsers(r.data.users || []);
  };
  useAutoRefresh(load, 15000);

  const act = async (uid, kind) => {
    await api.post(`/admin/kyc/${uid}/${kind}`);
    load();
  };

  return (
    <Page title={t("adm.kyc.title")} sub="Модуль временно закрыт" testId="admin-kyc-page">
      <Card data-testid="admin-kyc-locked-notice" style={{
        padding: 36, textAlign: "center",
        background: "linear-gradient(135deg, rgba(255,215,0,0.08), rgba(0,242,255,0.04))",
        border: "1px dashed rgba(255,215,0,0.4)",
      }}>
        <div style={{ fontSize: 56 }}>🔒</div>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: "#FFD700", marginTop: 12 }}>KYC-верификация временно закрыта</h3>
        <p style={{ fontSize: 14, color: "var(--tg-text-dim)", lineHeight: 1.7, maxWidth: 560, margin: "16px auto 0" }}>
          Верификацию KYC нужно будет проходить после старта проекта и переноса всей базы на собственный блокчейн.
        </p>
      </Card>
    </Page>
  );
}

/* ════════ 4. TRANSACTIONS ════════ */
export function AdminTransactionsPage() {
  const { t } = useT();
  const [txs, setTxs] = useState([]);
  const [type, setType] = useState("");
  const [total, setTotal] = useState(0);
  const load = async (tt = "") => {
    const r = await api.get(`/admin/transactions?limit=200${tt ? `&type_=${encodeURIComponent(tt)}` : ""}`);
    setTxs(r.data.transactions || []); setTotal(r.data.total || 0);
  };
  useEffect(() => { load(""); }, []);
  // Auto-refresh with current filter
  useAutoRefresh(() => load(type), 15000);

  return (
    <Page title={t("adm.tx.title")} sub={`${t("common.total")}: ${total}`} testId="admin-tx-page">
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--tg-text-dim)" }}>{t("adm.tx.filter")}</span>
          {["", "airdrop_magic44", "marketplace_checkout", "admin_adjust", "referral_bonus"].map((tt) => (
            <button key={tt} onClick={() => { setType(tt); load(tt); }} data-testid={`admin-tx-filter-${tt || "all"}`}
              style={{ padding: "6px 12px", borderRadius: 16, fontSize: 11, fontWeight: 700, cursor: "pointer",
                border: "1px solid " + (type === tt ? "var(--tg-blue)" : "var(--tg-border)"),
                background: type === tt ? "rgba(0,242,255,0.12)" : "transparent",
                color: type === tt ? "var(--tg-blue)" : "var(--tg-text-dim)" }}>
              {tt || "all"}
            </button>
          ))}
        </div>
      </Card>
      <Card>
        <table className="dash-table">
          <thead><tr><th>TX ID</th><th>User</th><th>{t("common.type")}</th><th>{t("common.amount")} TGC</th><th>Wallet</th><th>{t("common.date")}</th></tr></thead>
          <tbody>
            {txs.map((tx) => (
              <tr key={tx.txId}>
                <td style={{ fontFamily: "monospace", fontSize: 11, color: "var(--tg-blue)" }}>{tx.txId}</td>
                <td style={{ fontFamily: "monospace", fontSize: 11 }}>{tx.userId}</td>
                <td>{tx.type}</td>
                <td style={{ fontWeight: 700, color: tx.amount < 0 ? "#FF8FA6" : "#10F4A7" }}>{tx.amount > 0 ? "+" : ""}{fmt(tx.amount)}</td>
                <td style={{ fontSize: 11, color: "var(--tg-text-dim)" }}>{tx.wallet || "—"}</td>
                <td style={{ fontSize: 11, color: "var(--tg-text-dim)" }}>{tx.createdAt?.slice(0, 19).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

/* ════════ 5. ORDERS ════════ */
export function AdminOrdersPage() {
  const { t } = useT();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const loadOrders = async () => {
    const r = await api.get("/admin/orders?limit=100");
    setOrders(r.data.orders || []); setTotal(r.data.total || 0);
  };
  useAutoRefresh(loadOrders, 15000);

  return (
    <Page title={t("adm.orders.title")} sub={`${t("common.total")}: ${total}`} testId="admin-orders-page">
      <Card>
        <table className="dash-table">
          <thead><tr><th>Order ID</th><th>User</th><th>Items</th><th>EUR</th><th>TGC</th><th>Wallet</th><th>{t("common.date")}</th></tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.orderId}>
                <td style={{ fontFamily: "monospace", fontSize: 11, color: "var(--tg-blue)" }}>{o.orderId}</td>
                <td style={{ fontFamily: "monospace", fontSize: 11 }}>{o.userId}</td>
                <td style={{ fontSize: 12, color: "var(--tg-text-dim)" }}>{(o.items || []).map((i) => `${i.name}×${i.qty}`).join(", ")}</td>
                <td style={{ fontWeight: 700, color: "#FFD700" }}>€{fmt(o.totalEur)}</td>
                <td>{fmt(o.totalTGC)}</td>
                <td>{o.wallet}</td>
                <td style={{ fontSize: 11, color: "var(--tg-text-dim)" }}>{o.createdAt?.slice(0, 19).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

/* ════════ 6. PRODUCTS CATALOG ════════ */
export function AdminProductsPage() {
  const { t } = useT();
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", category: "Electronics", priceEur: "", image: "" });

  const load = async () => {
    const r = await api.get("/marketplace/products");
    setProducts(r.data.products || []);
  };
  useAutoRefresh(load, 15000);

  const create = async () => {
    if (!form.name || !form.priceEur) return;
    try {
      await api.post("/admin/products", { ...form, priceEur: Number(form.priceEur) });
      setForm({ name: "", category: "Electronics", priceEur: "", image: "" });
      load();
    } catch (e) { alert(e.response?.data?.detail || t("common.error")); }
  };

  const del = async (pid) => {
    if (!window.confirm(`${t("btn.delete")} ${pid}?`)) return;
    try { await api.delete(`/admin/products/${pid}`); load(); }
    catch (e) { alert(e.response?.data?.detail || t("common.error")); }
  };

  return (
    <Page title={t("adm.cat.title")} sub={`${t("common.total")}: ${products.length}`} testId="admin-products-page">
      <Card style={{ marginBottom: 18 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 14 }}>{t("adm.cat.add")}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 2fr auto", gap: 8 }}>
          <input className="auth-input" placeholder={t("adm.cat.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-prod-name" />
          <select className="auth-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-prod-cat">
            <option>Electronics</option><option>Fashion</option><option>Grocery</option><option>Auto</option><option>Service</option>
          </select>
          <input className="auth-input" placeholder={t("adm.cat.price")} type="number" value={form.priceEur} onChange={(e) => setForm({ ...form, priceEur: e.target.value })} data-testid="admin-prod-price" />
          <input className="auth-input" placeholder={t("admin.396925")} value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
          <button className="tg-btn primary" onClick={create} data-testid="admin-prod-add"><Plus size={14} /></button>
        </div>
      </Card>

      <Card>
        <table className="dash-table">
          <thead><tr><th>ID</th><th>{t("adm.cat.name")}</th><th>Category</th><th>EUR</th><th>TGC</th><th></th></tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.productId}>
                <td style={{ fontFamily: "monospace", fontSize: 11, color: "var(--tg-blue)" }}>{p.productId}</td>
                <td style={{ fontWeight: 600 }}>{p.name}</td>
                <td>{p.category}</td>
                <td style={{ fontWeight: 700, color: "#FFD700" }}>€{p.priceEur}</td>
                <td>{fmt(p.priceTGC)}</td>
                <td><button className="dash-icon-btn" onClick={() => del(p.productId)} data-testid={`admin-prod-del-${p.productId}`} style={{ color: "#FF3D6C" }}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}

/* ════════ 7. ADMIN LEADERBOARD ════════ */
export function AdminLeaderboardPage() {
  const [metric, setMetric] = useState("personal");
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/leaderboard?metric=${metric}&limit=${limit}`);
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [metric, limit]);
  useAutoRefresh(load, 15000);

  const RANGES = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  const leaders = data?.leaders || [];

  return (
    <Page title="🏆 Лидерборд" sub={`Сортировка: ${metric === "personal" ? "по личникам (L1)" : "по всей команде (L1..L8)"} · показано ${leaders.length}`} testId="admin-leaderboard-page">
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }} data-testid="adm-lb-tabs">
          {[
            { id: "personal", label: "Топ по личникам", icon: <Users size={14} /> },
            { id: "team",     label: "Топ по команде", icon: <Award size={14} /> },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setMetric(tab.id)} data-testid={`adm-lb-tab-${tab.id}`}
              style={{
                padding: "9px 16px", borderRadius: 10, cursor: "pointer",
                border: `1px solid ${metric === tab.id ? "var(--tg-blue)" : "var(--tg-border)"}`,
                background: metric === tab.id ? "rgba(0,242,255,0.12)" : "transparent",
                color: metric === tab.id ? "var(--tg-blue)" : "var(--tg-text-dim)",
                fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 8,
              }}>{tab.icon} {tab.label}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }} data-testid="adm-lb-ranges">
          {RANGES.map((n) => (
            <button key={n} onClick={() => setLimit(n)} data-testid={`adm-lb-range-${n}`}
              style={{
                padding: "5px 11px", borderRadius: 999, cursor: "pointer",
                border: `1px solid ${limit === n ? "#FFD700" : "var(--tg-border)"}`,
                background: limit === n ? "rgba(255,215,0,0.1)" : "transparent",
                color: limit === n ? "#FFD700" : "var(--tg-text-dim)",
                fontWeight: 700, fontSize: 11,
              }}>Топ-{n}</button>
          ))}
        </div>
      </Card>

      <Card style={{ padding: 0, overflowX: "auto" }}>
        {loading && <div style={{ padding: 14, color: "var(--tg-text-hint)" }}>Загрузка...</div>}
        <table className="dash-table" data-testid="adm-lb-table">
          <thead>
            <tr>
              <th>#</th><th>UserID</th><th>Имя</th><th>Email</th><th>Телефон</th><th>Страна</th><th>L1 личн.</th><th>Команда</th><th>Регистрация</th>
            </tr>
          </thead>
          <tbody>
            {leaders.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: "center", padding: 28, color: "var(--tg-text-hint)" }}>Нет данных</td></tr>
            )}
            {leaders.map((r) => (
              <tr key={r.userId} data-testid={`adm-lb-row-${r.rank}`}>
                <td style={{ fontWeight: 900, color: r.rank === 1 ? "#FFD700" : r.rank === 2 ? "#C0C0C0" : r.rank === 3 ? "#CD7F32" : "var(--tg-text-dim)" }}>
                  {r.rank <= 3 && <Trophy size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />}
                  {r.rank}
                </td>
                <td style={{ fontFamily: "monospace", fontSize: 11, color: "var(--tg-blue)" }}>{r.userId}</td>
                <td style={{ fontWeight: 600 }}>{r.firstName} {r.lastName}</td>
                <td style={{ fontSize: 12 }}>{r.email}</td>
                <td style={{ fontSize: 12, color: "var(--tg-text-dim)" }}>{r.phone || "—"}</td>
                <td style={{ fontSize: 13 }}>
                  <span style={{ fontSize: 16, marginRight: 6 }}>{flagOf(r.country)}</span>
                  <span style={{ color: "var(--tg-text-dim)" }}>{findCountry(r.country).ru}</span>
                </td>
                <td style={{ fontWeight: 700, color: "#FFD700" }}>{fmtInt(r.directRefCount)}</td>
                <td style={{ fontWeight: 700, color: "#00F2FF" }}>{fmtInt(r.teamCount)}</td>
                <td style={{ fontSize: 11, color: "var(--tg-text-dim)" }}>
                  {r.createdAt ? new Date(r.createdAt).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Page>
  );
}


/* ════════ 8. INVESTOR LEADS INBOX (read-only) ════════ */
export function AdminInvestorLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/investor-leads?limit=200&unread_only=${unreadOnly}`);
      setLeads(r.data.leads || []); setTotal(r.data.total || 0); setUnread(r.data.unread || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [unreadOnly]);
  useAutoRefresh(() => load(), 15000);

  const openLead = async (lead) => {
    setSelected(lead);
    if (!lead.read) {
      try {
        await api.patch(`/admin/investor-leads/${lead.leadId}/read`);
        setLeads((prev) => prev.map((l) => l.leadId === lead.leadId ? { ...l, read: true } : l));
        setUnread((u) => Math.max(0, u - 1));
      } catch (_) { /* best-effort mark-read */ }
    }
  };

  const markUnread = async (leadId) => {
    try {
      await api.patch(`/admin/investor-leads/${leadId}/unread`);
      setLeads((prev) => prev.map((l) => l.leadId === leadId ? { ...l, read: false } : l));
      setUnread((u) => u + 1);
      setSelected(null);
    } catch (e) { alert(e.response?.data?.detail || "Ошибка"); }
  };

  const archive = async (leadId) => {
    if (!window.confirm("Скрыть эту заявку из списка? (данные останутся в БД)")) return;
    try {
      await api.delete(`/admin/investor-leads/${leadId}`);
      setSelected(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Ошибка"); }
  };

  return (
    <Page title="💼 Заявки инвесторов" sub={`Всего: ${total} · Непрочитано: ${unread}`} testId="admin-investor-leads-page">
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button onClick={() => setUnreadOnly(!unreadOnly)} data-testid="leads-unread-filter"
            style={{
              padding: "10px 14px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700,
              border: `1px solid ${unreadOnly ? "#00F2FF" : "var(--tg-border)"}`,
              background: unreadOnly ? "rgba(0,242,255,0.12)" : "transparent",
              color: unreadOnly ? "#00F2FF" : "var(--tg-text-dim)",
            }}>
            ✉ Только непрочитанные {unreadOnly && `(${unread})`}
          </button>
          <button onClick={load} className="tg-btn ghost" data-testid="leads-refresh">🔄 Обновить</button>
          <div style={{ fontSize: 12, color: "var(--tg-text-hint)", marginLeft: "auto" }}>
            Заявки поступают с сайта → /contact (форма для инвесторов)
          </div>
        </div>
      </Card>

      <Card>
        {loading && <div style={{ color: "var(--tg-text-hint)" }}>Загрузка…</div>}
        {!loading && leads.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--tg-text-hint)" }}>
            📭 {unreadOnly ? "Нет непрочитанных заявок" : "Пока ни одной заявки"}
          </div>
        )}
        {leads.length > 0 && (
          <table className="dash-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>Дата</th>
                <th>Имя</th>
                <th>Email</th>
                <th>Компания</th>
                <th>Сумма</th>
                <th>Сообщение</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.leadId} onClick={() => openLead(l)} data-testid={`lead-row-${l.leadId}`}
                  style={{ cursor: "pointer", background: !l.read ? "rgba(0,242,255,0.05)" : "transparent" }}>
                  <td>{!l.read && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#00F2FF" }} title="Непрочитано" />}</td>
                  <td style={{ fontSize: 12, color: "var(--tg-text-dim)", whiteSpace: "nowrap" }}>
                    {l.createdAt ? new Date(l.createdAt).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </td>
                  <td style={{ fontWeight: !l.read ? 700 : 500 }}>{l.name}</td>
                  <td style={{ fontSize: 13, color: "var(--tg-blue)" }}>{l.email}</td>
                  <td style={{ fontSize: 13, color: "var(--tg-text-dim)" }}>{l.company || "—"}</td>
                  <td style={{ fontSize: 12, color: "#FFD700", fontWeight: 600 }}>{l.amount || "—"}</td>
                  <td style={{ fontSize: 12, color: "var(--tg-text-dim)", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 40, overflowY: "auto" }}
          onClick={() => setSelected(null)} data-testid="lead-modal">
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#0B1220", border: "1px solid #00F2FF", borderRadius: 14, padding: 28, width: "min(680px, 100%)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, color: "#00F2FF", fontSize: 20 }}>💼 Заявка инвестора</h3>
                <div style={{ fontSize: 12, color: "var(--tg-text-hint)", marginTop: 4 }}>
                  {selected.createdAt ? new Date(selected.createdAt).toLocaleString("ru-RU") : "—"} · ID {selected.leadId}
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: "transparent", border: "none", color: "#888", fontSize: 26, cursor: "pointer" }}>×</button>
            </div>

            <div style={{ display: "grid", gap: 12, fontSize: 14 }}>
              <div><span style={{ color: "var(--tg-text-hint)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Имя</span><div style={{ fontWeight: 600 }}>{selected.name}</div></div>
              <div><span style={{ color: "var(--tg-text-hint)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Email</span>
                <div><a href={`mailto:${selected.email}`} style={{ color: "var(--tg-blue)", fontWeight: 600 }}>{selected.email}</a></div>
              </div>
              {selected.company && <div><span style={{ color: "var(--tg-text-hint)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Компания</span><div>{selected.company}</div></div>}
              {selected.amount && <div><span style={{ color: "var(--tg-text-hint)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Сумма</span><div style={{ color: "#FFD700", fontWeight: 700 }}>{selected.amount}</div></div>}
              <div>
                <span style={{ color: "var(--tg-text-hint)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Сообщение</span>
                <div style={{ padding: 14, background: "rgba(0,0,0,0.4)", border: "1px solid var(--tg-border)", borderRadius: 8, marginTop: 6, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                  {selected.message}
                </div>
              </div>
              {(selected.ip || selected.userAgent) && (
                <details style={{ fontSize: 11, color: "var(--tg-text-hint)" }}>
                  <summary style={{ cursor: "pointer" }}>Технические данные</summary>
                  <div style={{ marginTop: 6, fontFamily: "monospace" }}>
                    IP: {selected.ip || "—"}<br />
                    UA: {selected.userAgent || "—"}
                  </div>
                </details>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 20, borderTop: "1px solid var(--tg-border)", paddingTop: 16, flexWrap: "wrap" }}>
              <a className="tg-btn primary" href={`mailto:${selected.email}?subject=${encodeURIComponent("Re: заявка на инвестиции — Turbo Global Coin")}`}
                data-testid="lead-reply-mailto" style={{ flex: 1, minWidth: 140, textDecoration: "none", justifyContent: "center" }}>
                ✉ Ответить по email
              </a>
              <button className="tg-btn ghost" onClick={() => markUnread(selected.leadId)} data-testid="lead-mark-unread"
                style={{ flex: 1, minWidth: 140 }}>
                📩 Пометить непрочитанным
              </button>
              <button className="tg-btn ghost" onClick={() => archive(selected.leadId)} data-testid="lead-archive"
                style={{ flex: 1, minWidth: 120, borderColor: "#FF3D6C", color: "#FF3D6C" }}>
                🗑 Скрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
